import DOMPurify from 'dompurify';

/**
 * Sanityzuje tekst usuwając wszystkie tagi HTML i niebezpieczne znaki
 * @param {string} text - Tekst do oczyszczenia
 * @returns {string} - Oczyszczony tekst
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';

  // Usuń wszystkie tagi HTML, zostaw tylko czysty tekst
  return DOMPurify.sanitize(text, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true 
  }).trim();
};

/**
 * Sanityzuje URL sprawdzając czy jest bezpieczny
 * @param {string} url - URL do sprawdzenia
 * @returns {string} - Bezpieczny URL lub pusty string
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();

  // Sprawdź czy URL zaczyna się od bezpiecznych protokołów
  if (!trimmed.match(/^https?:\/\//i)) {
    return '';
  }

  // Sanityzuj URL
  const sanitized = DOMPurify.sanitize(trimmed, { 
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [] 
  });

  // Dodatkowa walidacja URL
  try {
    const urlObj = new URL(sanitized);
    // Zezwól tylko na http i https
    if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
      return sanitized;
    }
    return '';
  } catch (e) {
    return '';
  }
};

/**
 * Sanityzuje dane przepisu przed zapisem do bazy
 * @param {Object} recipe - Obiekt przepisu
 * @returns {Object} - Oczyszczony obiekt przepisu
 */
export const sanitizeRecipeData = (recipe) => {
  return {
    title: sanitizeText(recipe.title),
    time: sanitizeText(recipe.time),
    link: sanitizeUrl(recipe.link),
    ingredients: Array.isArray(recipe.ingredients) 
      ? recipe.ingredients.map(ing => {
          if (typeof ing === 'string') {
            return sanitizeText(ing);
          }
          return {
            name: sanitizeText(ing.name),
            isMain: !!ing.isMain
          };
        }).filter(ing => typeof ing === 'string' ? ing : ing.name)
      : [],
    steps: Array.isArray(recipe.steps)
      ? recipe.steps.map(step => sanitizeText(step)).filter(Boolean)
      : [],
    tags: Array.isArray(recipe.tags)
        ? recipe.tags.map(tag => sanitizeText(tag)).filter(Boolean)
        : [],
    createdAt: recipe.createdAt || new Date().toISOString(),
    source: recipe.source ? sanitizeText(recipe.source) : null,
      userId: recipe.userId || null,
    lang: sanitizeText(recipe.lang) || 'pl'
  };
};

/**
 * Sanityzuje tablicę stringów (np. składników, kroków)
 * @param {Array<string>} items - Tablica stringów do oczyszczenia
 * @returns {Array<string>} - Oczyszczona tablica
 */
export const sanitizeArray = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => sanitizeText(item)).filter(Boolean);
};
